import { FeatureCard, Button, Colors, Text } from '@rockalingua/ui';
import {
  EyeOutlined,
  CheckCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';

import {
  subscriptionTypes,
  yearDiscount,
} from '@rockalingua/shared/subscriptions/constants';
import usePaymentFlowAnalytics from '@/v2/domains/Analytics/hooks/usePaymentFlowAnalytics.hook';
import {
  PaymentFlowStep,
  PaymentSection,
} from '@/v2/domains/Analytics/types/payment';
import { useExpiredSubscriptionModalStore } from '@/v2/domains/Subscriptions/stores/useExpiredSubscriptionModal.store';
import useManageSubscriptionActions from '@/v2/domains/Subscriptions/hooks/useManageSubscriptionActions.hook';
import useGetSubscriptionTypesQuery from '@/v2/domains/Subscriptions/queries/useGetSubscriptionTypesQuery.hook';
import type { SubscriptionType } from '@/v2/domains/Subscriptions/types/subscription';
import ExpiredSubscriptionContainer from '../ExpiredSubscriptionBannerContainer/ExpiredSubscriptionContainer';
import { useTrialOrPaidText } from '../../hooks/useTrialOrPaidText.hook';

import {
  FeaturesSection,
  ButtonContainer,
  PrimaryButtonBlock,
  PriceContainer,
  SecondaryButtonBlock,
} from './TeacherExpiredSubscriptionBanner.styled';

type TeacherExpiredSubscriptionBannerProps = {
  title?: string;
  size?: 'large' | 'small';
};

export default function TeacherExpiredSubscriptionBanner({
  title,
  size = 'large',
}: TeacherExpiredSubscriptionBannerProps) {
  const { onOptionSelected, onSkip } = usePaymentFlowAnalytics({
    section: PaymentSection.paywall,
    step: PaymentFlowStep.payClicked,
  });
  const { subscribe } = useManageSubscriptionActions();
  const { setModalClosed } = useExpiredSubscriptionModalStore();
  const defaultTitle = useTrialOrPaidText();

  const { data } = useGetSubscriptionTypesQuery();
  const { teacher } = getBannerSubscriptionTypes(data as SubscriptionType[]);

  const handleContinue = () => {
    onOptionSelected({ option: teacher.code });
    setModalClosed(true);

    subscribe();
  };

  return (
    <ExpiredSubscriptionContainer
      title={title || defaultTitle}
      subtitle="You've explored Rockalingua's content for teaching Spanish. To keep using videos, games, and activities, choose how you'd like to continue."
      size={size}
    >
      <FeaturesSection>
        {features.map((feature) => (
          <FeatureCard
            key={feature.title}
            title={feature.title}
            subtitle={feature.subtitle}
            iconBgColor={feature.iconBgColor}
            icon={feature.icon}
          />
        ))}
      </FeaturesSection>

      <ButtonContainer>
        <PrimaryButtonBlock>
          <Button
            type="primary"
            size={size}
            color="accent"
            onClick={handleContinue}
          >
            Continue with {teacher.name}
          </Button>
          <PriceContainer>
            <Text variant="grey-16-medium" color="black">
              ${teacher.monthPrice}/month or ${teacher.yearPrice}
              /year (Save up to {yearDiscount})
            </Text>
            <Text variant="dm-14-regular" color={Colors.NEUTRAL_VARIANTS[90]}>
              Cancel anytime
            </Text>
          </PriceContainer>
        </PrimaryButtonBlock>
        {size == 'large' && (
          <SecondaryButtonBlock>
            <Button
              type="link"
              onClick={() => {
                onSkip();
                setModalClosed(true);
              }}
              size={size}
            >
              Leave for now
            </Button>
          </SecondaryButtonBlock>
        )}
      </ButtonContainer>
    </ExpiredSubscriptionContainer>
  );
}

const features = [
  {
    title: 'Full access to all Rockalingua content',
    subtitle: '',
    iconBgColor: Colors.ACCENT_VARIANTS[30],
    icon: <EyeOutlined style={{ color: Colors.ACCENT_VARIANTS[90] }} />,
  },
  {
    title: 'Videos, games, songs, and activities',
    subtitle: '',
    iconBgColor: Colors.SUCCESS_VARIANTS[30],
    icon: (
      <CheckCircleOutlined style={{ color: Colors.SUCCESS_VARIANTS[70] }} />
    ),
  },
  {
    title: 'Use content freely in your teaching',
    subtitle: '',
    iconBgColor: Colors.MAIN_VARIANTS[30],
    icon: <SyncOutlined style={{ color: Colors.MAIN_VARIANTS[70] }} />,
  },
];

function getBannerSubscriptionTypes(
  dynamicSubscriptionTypes: SubscriptionType[] = []
): {
  teacher: SubscriptionType;
} {
  const teacher =
    dynamicSubscriptionTypes.find(
      (subscriptionType): subscriptionType is SubscriptionType =>
        subscriptionType?.code == subscriptionTypes.teacher.code
    ) ?? subscriptionTypes.teacher;

  return {
    teacher,
  };
}
